import React, { useState, useEffect, useRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Clock, Eye, Play, Square, UserCheck } from "lucide-react";
import { IsolatedStageTimer } from "@/components/isolated-stage-timer";
import { formatTimer } from "@/lib/timer-utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ParticipantActionsDropdown from "@/components/participant-actions-dropdown";
import type { Performer, JamNight } from "@shared/schema";
import type { ParticipantMode, ModeConfig } from "./mode-config";
import {
  ParticipantBadge,
  InstrumentBadges,
  TypeBadge,
  TierBadge,
  PriorityBadge,
  UpNextBadge
} from "./participant-badge";

interface ParticipantMobileCardProps {
  performer: Performer;
  mode: ParticipantMode;
  config: ModeConfig;
  onActionToggle: (performer: Performer) => void;
  onConsider?: (performer: Performer) => void;
  onRemove: (performerId: number) => void;
  isLoading: boolean;
  disabled?: boolean;
  jamNightId?: number;
  jamNight?: JamNight;
  isFirst?: boolean;
}

export const ParticipantMobileCard = React.memo(function ParticipantMobileCard({
  performer,
  mode,
  config,
  onActionToggle,
  onConsider,
  onRemove,
  isLoading,
  disabled = false,
  jamNightId,
  jamNight,
  isFirst = false,
}: ParticipantMobileCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: performer.id });

  const [recentlyDropped, setRecentlyDropped] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isDragging && timeoutRef.current === null) {
      setRecentlyDropped(true);
      timeoutRef.current = setTimeout(() => {
        setRecentlyDropped(false);
        timeoutRef.current = null;
      }, 100);
    } else if (isDragging) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setRecentlyDropped(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isDragging]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: (isDragging || recentlyDropped) ? 'none' : transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative" as const,
    zIndex: isDragging ? 50 : "auto",
    willChange: isDragging ? 'transform' : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border-b rounded-lg mb-2 ${
        isDragging 
          ? "bg-white border-slate-300 shadow-lg z-50" 
          : "bg-white/20 border-white/20"
      } last:border-b-0 p-4`}
    >
      <div
        {...(disabled ? {} : attributes)}
        {...(disabled ? {} : listeners)}
        className={`absolute left-0 top-0 h-full w-1/4 ${disabled ? 'cursor-not-allowed' : 'cursor-grab'} touch-none`}
        style={{ touchAction: "none" }}
      />

      {/* Up Next Banner (Open Mic only) */}
      {config.isOpenMic && isFirst && (
        <div className="bg-blue-100 border-l-4 border-blue-500 p-2 mb-3 rounded-r">
          <div className="flex items-center">
            <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
            <span className="text-blue-800 font-medium text-sm">Up Next</span>
          </div>
        </div>
      )}

        {/* Priority Banner */}
        {performer.priority && (
            <div className="bg-orange-100 border-l-4 mb-2 border-orange-500 p-2 rounded-r">
                <div className="flex items-center">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mr-2 animate-pulse"></div>
                    <span className="text-orange-800 font-medium text-sm">Priority Participant</span>
                </div>
            </div>
        )}

      {/* Top row - Drag handle, name, and dropdown menu only */}
      <div className="flex items-start justify-between ">
        <div className="flex items-start flex-1 min-w-0">
          <div
            className={`mr-3 mt-1 ${disabled ? 'text-slate-400 opacity-50 cursor-not-allowed' : 'text-slate-400 hover:text-slate-600'}`}
            style={{ touchAction: "none" }}
          >
            <GripVertical className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0 mb-2">
            <span className="text-lg font-medium text-slate-900/90 break-words drop-shadow-sm ">
              {performer.name}
            </span>
          </div>
        </div>
        {/* Dropdown menu only */}
        <ParticipantActionsDropdown
          performer={performer}
          onRemove={onRemove}
          disabled={disabled}
          isLoading={isLoading}
          jamNight={jamNight}
          jamNightId={jamNightId}
          isDragging={isDragging}
        />
      </div>

      {/* Content section */}
      <div className="space-y-2">
        {/* Badges row */}
        <div className="flex flex-wrap gap-">
          {/* Type (Open Mic) */}
          {config.isOpenMic && (
            <TypeBadge performerType={performer.performerType} isDragging={isDragging} />
          )}

          {/* On Stage Badge */}
          {performer.status === 'on_stage' && (
            <Badge className="bg-green-100/40 backdrop-blur-sm text-green-800 border-green-200/50 shadow-sm">
              <div className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></div>
              On Stage
            </Badge>
          )}
        </div>

        {/* Time Display (Open Mic) */}
        {config.isOpenMic && (
          <div className="flex items-center" style={{ touchAction: 'auto' }}>
            <Clock className="h-4 w-4 text-slate-400 mr-2" />
            <span className="text-sm text-slate-600">
              {formatTimer((performer.timeOnStage || 0) * 60)} total
            </span>
          </div>
        )}

        {/* Timer Display (Jam Night - when on stage) */}
        {config.isJamNight && (performer.status === 'on_stage' || performer.status === 'pending_removal') && (
          <div className="text-sm text-slate-600">
            <Clock className="inline h-3 w-3 text-warning mr-1" />
            <IsolatedStageTimer
              stageStartTime={performer.stageStartTime}
              className="font-mono font-medium text-warning"
            />
          </div>
        )}
      </div>

      {/* Performance Info (Jam Night) */}

        <div className="pt-0">
          <div className="text-sm font-medium text-slate-900 flex items-center gap-1">
              <TierBadge tier={performer.tier} />

              <ParticipantBadge performer={performer} mode={mode} isDragging={isDragging} />

              {config.isJamNight && performer.played && (
              <span>Time: {performer.timeOnStage} min</span>
                  )}
          </div>
        </div>


      {/* Instruments (Jam Night) - Full width */}
      {config.isJamNight && performer.instruments && performer.instruments.length > 0 && (
        <div className="mt-2 ">
          <InstrumentBadges instruments={performer.instruments} isDragging={isDragging} />
        </div>
      )}



      {/* Action buttons at the bottom */}
      {performer.status !== 'on_stage' && (
        <div className="mt-0 pt-3 ">
          <div className={`grid gap-2 ${mode === 'jam-night' && performer.status === 'participant' && onConsider ? 'grid-cols-2' : 'grid-cols-1'}`}>

            {/* Consider button for Jam Night */}
            {mode === 'jam-night' && performer.status === 'participant' && onConsider && (
              <Button
                onClick={() => onConsider(performer)}
                disabled={disabled || isLoading}
                className="w-full bg-yellow-600/80 backdrop-blur-sm text-white hover:bg-yellow-700/90 border border-yellow-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  boxShadow: '0 4px 16px rgba(251,191,36,0.15), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
              >
                <Eye className="h-3 w-3 mr-1" />
                Consider
              </Button>
            )}

              {/* Main action button */}
              <Button
                  onClick={() => onActionToggle(performer)}
                  disabled={disabled || isLoading}
                  className="w-full bg-green-600/80 backdrop-blur-sm text-white hover:bg-green-700/90 border border-green-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                      boxShadow: '0 4px 16px rgba(34,197,94,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
              >
                  {config.isGenericEvent ? (
                      <>
                          <UserCheck className="h-3 w-3 mr-1" />
                          Mark Attended
                      </>
                  ) : (
                      <>
                          <Play className="h-3 w-3 mr-1" />
                          {mode === 'open-mic' ? 'On Stage' : 'Select'}
                      </>
                  )}
              </Button>

          </div>
        </div>
      )}

      {/* Off Stage button for performers on stage */}
      {performer.status === 'on_stage' && (
        <div className="mt-3 pt-3 border-t border-slate-200/50">
          <Button
            onClick={() => onActionToggle(performer)}
            disabled={disabled || isLoading}
            className="w-full bg-red-500/80 backdrop-blur-sm text-white hover:bg-red-600/90 border border-red-400/30 shadow-lg transition-all duration-300"
            style={{
              boxShadow: '0 4px 16px rgba(220,38,38,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
            }}
          >
            <Square className="h-3 w-3 mr-1" />
            Off Stage
          </Button>
        </div>
      )}
    </div>
  );
});